import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { COUNTRIES } from '../config/feeds';
import { useI18n } from '../i18n';
import { openInAppBrowser } from '../utils/browser';
import Modal from './Modal';
import { ExternalLink, Globe, ChevronRight } from 'lucide-react';

const CountryPicker = ({ currentCountry, onCountryChange, isFirstTime = false }) => {
  const [isOpen, setIsOpen] = useState(isFirstTime);
  const [showLanguageSelector, setShowLanguageSelector] = useState(false);
  const [onboardingStep, setOnboardingStep] = useState('language'); // 'language' or 'country'
  const [hasSelectedLanguage, setHasSelectedLanguage] = useState(false);
  
  const { t, language, setLanguage, availableLanguages, currentLanguage } = useI18n();

  const handleCountrySelect = (countryId) => {
    onCountryChange(countryId);
    setIsOpen(false);
  };

  const handleLanguageSelect = (langCode) => {
    setLanguage(langCode);
    setHasSelectedLanguage(true);
    if (isFirstTime) {
      setOnboardingStep('country');
    } else {
      setShowLanguageSelector(false);
    }
  };

  const handleExternalLink = (e, url) => {
    e.preventDefault();
    openInAppBrowser(url);
  };

  // For onboarding: don't show active state until user has selected
  const OnboardingLanguageList = () => (
    <div className="language-grid">
      {availableLanguages.map((lang) => (
        <button
          key={lang.code}
          className={`language-button ${hasSelectedLanguage && language === lang.code ? 'active' : ''}`}
          onClick={() => handleLanguageSelect(lang.code)}
        >
          <span className="language-flag">{lang.flag}</span>
          <span className="language-native-name">{lang.nativeName}</span>
          <span className="language-name">{lang.name}</span>
        </button>
      ))}
    </div>
  );

  // For onboarding: don't show active state (since no country is selected yet)
  const OnboardingCountryList = () => (
    <div className="country-grid">
      {Object.values(COUNTRIES).map((country) => (
        <button
          key={country.id}
          className="country-button"
          onClick={() => handleCountrySelect(country.id)}
        >
          <span className="country-flag">{country.flag}</span>
          <span className="country-name">{country.name}</span>
        </button>
      ))}
    </div>
  );

  // Regular lists with active state for modal (when country is already selected)
  const LanguageList = () => (
    <div className="language-grid">
      {availableLanguages.map((lang) => (
        <button
          key={lang.code}
          className={`language-button ${language === lang.code ? 'active' : ''}`}
          onClick={() => handleLanguageSelect(lang.code)}
        >
          <span className="language-flag">{lang.flag}</span>
          <span className="language-native-name">{lang.nativeName}</span>
          <span className="language-name">{lang.name}</span>
        </button>
      ))}
    </div>
  );

  const CountryList = () => (
    <div className="country-grid">
      {Object.values(COUNTRIES).map((country) => (
        <button
          key={country.id}
          className={`country-button ${currentCountry === country.id ? 'active' : ''}`}
          onClick={() => handleCountrySelect(country.id)}
        >
          <span className="country-flag">{country.flag}</span>
          <span className="country-name">{country.name}</span>
        </button>
      ))}
    </div>
  );

  // First time onboarding flow
  if (isFirstTime) {
    return (
      <div className="country-picker-modal">
        <div className="country-picker-header">
          <img className="logo" src="logo192.png" alt="Govsky Logo" />
          <h2>{t('onboarding.welcome')}</h2>
          <h3>{t('onboarding.subtitle')}</h3>
        </div>
        
        <div className="country-picker-scroll">
          {onboardingStep === 'language' ? (
            <>
              <p className="select-prompt">{t('onboarding.selectLanguage')}</p>
              <OnboardingLanguageList />
            </>
          ) : (
            <>
              <p className="select-prompt">{t('onboarding.selectCountry')}</p>
              <OnboardingCountryList />
              
              {/* Option to go back to language selection */}
              <button 
                className="change-language-button"
                onClick={() => setOnboardingStep('language')}
              >
                <Globe size={16} />
                {t('countryPicker.changeLanguage')}: {currentLanguage?.nativeName}
              </button>
            </>
          )}
        </div>
        
        <div className="country-picker-footer">
          <p className="onboarding-legal-text">
            {t('onboarding.ageConfirm')} <Link to="/privacy">{t('onboarding.privacyLink')}</Link>
          </p>
          <button 
            className="foundation-link"
            onClick={(e) => handleExternalLink(e, 'https://govglance.foundation')}
          >
            <ExternalLink size={14} />
            {t('onboarding.poweredBy')} govglance.foundation
          </button>
        </div>
      </div>
    );
  }

  // Regular country/language picker (in header)
  return (
    <>
      <button
        className="current-country"
        onClick={() => setIsOpen(true)}
        aria-label={t('countryPicker.selectCountry')}
      >
        <span className="country-flag">{COUNTRIES[currentCountry].flag}</span>
        <span className="country-name">{COUNTRIES[currentCountry].name}</span>
      </button>

      {isOpen && (
        <Modal 
          isOpen={isOpen} 
          onClose={() => {
            setIsOpen(false);
            setShowLanguageSelector(false);
          }}
        >
          <div className="country-picker-content modal-version">
            {showLanguageSelector ? (
              <>
                <h2>{t('countryPicker.language')}</h2>
                <LanguageList />
                <button 
                  className="back-button"
                  onClick={() => setShowLanguageSelector(false)}
                >
                  {t('common.back')}
                </button>
              </>
            ) : (
              <>
                <h2>{t('countryPicker.selectCountry')}</h2>
                <CountryList />
                
                <div className="language-section">
                  <button 
                    className="language-switcher-button"
                    onClick={() => setShowLanguageSelector(true)}
                  >
                    <Globe size={18} />
                    <span className="language-switcher-label">
                      {t('countryPicker.language')}: {currentLanguage?.nativeName}
                    </span>
                    <ChevronRight size={18} />
                  </button>
                </div>
              </>
            )}
          </div>
        </Modal>
      )}
    </>
  );
};

export default CountryPicker;
